import InstagramLogoIcon from 'assets/icons/InstagramLogoIcon';
import React, { useEffect, useMemo, useState } from 'react'
import Avatar from 'react-avatar';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { fetchSocialProfiles } from 'services/api/social-account';
import { RootState } from 'store';
import { SocialAccount } from 'types/influencer';
import { formatNumber } from 'utils/helpers';

const AISuggestions: React.FC<{ step: string, onClick: (value: string) => void, activeSuggestion?: string }> = ({ step, onClick, activeSuggestion }) => {
    const suggestions = useSelector((state: RootState) => state.auth.metadata_suggestions[step]);
    const [loading, setLoading] = useState(false);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
    const competitorsData = useSelector((state: RootState) => state.competitionAnalysis.competitors);
    const competitors = (competitorsData?.items ?? []).filter((c) => c.via !== 'self');

    const fetchSocialAccounts = async (platform: string, handles: string[]) => {
        if (!selectedAgencyId) return;
        try {
            setLoading(true);
            const response = await fetchSocialProfiles(selectedAgencyId, platform, handles);
            if (Array.isArray(response?.items)) {
                setSocialAccounts(response.items);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const isSocialAccountStep = useMemo(() => {
        return ['competitors', 'ig'].includes(step);
    }, [step])

    useEffect(() => {
        if (isSocialAccountStep && suggestions?.length > 0 && selectedAgencyId) {
            fetchSocialAccounts('ig', suggestions);
        }
    }, [step, suggestions, selectedAgencyId])

    if (!Array.isArray(suggestions) || suggestions.length === 0) {
        return null;
    }

    if (isSocialAccountStep && !loading && socialAccounts.length === 0) {
        return null;
    }

    return (
        <div className="onboarding-step-suggestions-wrapper">
            <div className="onboarding-step-suggestions gap-4">
                <div className='d-flex align-items-center gap-3'>
                    <span>
                        <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" y="0.5" width="45" height="45" rx="22.5" fill="url(#paint0_linear_3822_5782)" />
                            <rect x="0.5" y="0.5" width="45" height="45" rx="22.5" stroke="#EF4D81" />
                            <path d="M23 31.8564C22.065 27.424 18.5768 23.9354 14.1445 23C18.5765 22.0647 22.0647 18.5765 23 14.1445C23.9354 18.5768 27.424 22.065 31.8564 23C27.4237 23.9351 23.9351 27.4237 23 31.8564Z" fill="url(#paint1_linear_3822_5782)" stroke="#FFCFDE" strokeWidth="0.5" />
                            <path d="M32 17.1729C31.6485 15.5946 30.4063 14.3518 28.8281 14C30.4059 13.6483 31.6483 12.4059 32 10.8281C32.3518 12.4063 33.5946 13.6485 35.1729 14C33.5943 14.3515 32.3515 15.5943 32 17.1729Z" fill="url(#paint2_linear_3822_5782)" stroke="#FFCFDE" strokeWidth="0.2" />
                            <defs>
                                <linearGradient id="paint0_linear_3822_5782" x1="23" y1="0" x2="23" y2="46" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#3C0118" />
                                    <stop offset="1" stopColor="#A20242" />
                                </linearGradient>
                                <linearGradient id="paint1_linear_3822_5782" x1="23.9167" y1="29.4167" x2="13.2222" y2="16.2778" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#FFABC2" />
                                    <stop offset="0.461569" stopColor="#FF4360" />
                                    <stop offset="0.694356" stopColor="#FF267D" />
                                </linearGradient>
                                <linearGradient id="paint2_linear_3822_5782" x1="32.3333" y1="16.3333" x2="28.4444" y2="11.5556" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#FFABC2" />
                                    <stop offset="0.461569" stopColor="#FF4360" />
                                    <stop offset="0.694356" stopColor="#FF267D" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </span>
                    <div className='d-flex flex-column'>
                        <h3 className='onboarding-ai-suggestion-title'>We found your {step === 'website_url' ? 'Website' : (step === 'ig' ? 'Instagram' : (step === 'competitors' ? 'Competitors' : ''))}</h3>
                        <span className='onboarding-ai-suggestion-subtitle'>Our <span className='ai-highlight'>AI</span> identified the following. Select your relevant {step === 'website_url' ? 'website link' : (step === 'ig' ? 'instagram handle' : (step === 'competitors' ? 'competitor handles' : ''))} or type above to proceed</span>
                    </div>
                </div>
                <div className='ai-suggestions-body'>
                    {
                        loading ? (
                            <Skeleton height={80} className='rounded-3 mb-3' count={3} />
                        ) : (
                            <>
                                {
                                    isSocialAccountStep ? (
                                        <div className="list-card-list">
                                            {
                                                (step === 'competitors' ? socialAccounts.filter((a) => !competitors.map((c) => c.social_account.handle).includes(a.handle)) : socialAccounts).sort((a, b) => (b?.ig?.followers_count ?? 0) - (a?.ig?.followers_count ?? 0)).map((socialAccount, i) => (
                                                    <div className={`ai-suggestion-item list-card-list-item cursor-pointer ${activeSuggestion === socialAccount.handle ? 'active' : ''}`} onClick={() => onClick(socialAccount.handle)} key={i}>
                                                        <div className="w-100 d-flex align-items-start align-items-md-center justify-content-between flex-column flex-md-row gap-3">
                                                            <div className="d-flex flex-column gap-2 w-50">
                                                                <div className="d-flex align-items center gap-2">
                                                                    {
                                                                        (socialAccount?.profile_picture_url) ? (
                                                                            <img className='social-account-img' style={{ height: '3.75rem', width: '3.75rem', borderRadius: '0.425rem' }} src={socialAccount.profile_picture_url} alt="" />
                                                                        ) : (
                                                                            <Avatar name={socialAccount.handle ?? ''} size="3.75rem" round />
                                                                        )
                                                                    }
                                                                    <div className={`d-flex flex-column justify-content-center gap-2`}>
                                                                        <div className="d-flex flex-column justify-content-center" style={{ gap: '0.12rem' }}>
                                                                            <span className="list-card-list-item-title">
                                                                                {
                                                                                    socialAccount.platform === 'ig' && socialAccount.name
                                                                                }
                                                                            </span>
                                                                            <span className="list-card-list-item-subtitle"> {socialAccount.platform === 'ig' ? <InstagramLogoIcon /> : <i className='ri-youtube-fill fs-16px text-danger'></i>} @{socialAccount.handle}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="profile-stats border-1">
                                                                <div className="row text-center g-0">
                                                                    <div className="col-4 stats-item p-2 px-3">
                                                                        <h4 className="profile-stat-value mb-1">{socialAccount.platform === 'ig' ? formatNumber(socialAccount.ig?.media_count ?? 0) : formatNumber(socialAccount.yt?.video_count ?? 0)}</h4>
                                                                        <p className="mb-0 profile-stat-label">{socialAccount.platform === 'ig' ? 'Posts' : 'Videos'}</p>
                                                                    </div>

                                                                    <div className="col-4 stats-item border-start border-end p-2 px-3">
                                                                        <h4 className="profile-stat-value mb-1">{socialAccount.platform === 'ig' ? formatNumber(socialAccount.ig?.followers_count ?? 0) : formatNumber(socialAccount.yt?.subscriber_count ?? 0)}</h4>
                                                                        <p className="mb-0 profile-stat-label">{socialAccount.platform === 'ig' ? 'Followers' : 'Subscribers'}</p>
                                                                    </div>

                                                                    <div className="col-4 stats-item p-2 px-3">
                                                                        <h4 className="profile-stat-value mb-1">{socialAccount.platform === 'ig' ? formatNumber(socialAccount.ig?.follows_count ?? 0) : formatNumber(socialAccount.yt?.view_count ?? 0)}</h4>
                                                                        <p className="mb-0 profile-stat-label">{socialAccount.platform === 'ig' ? 'Following' : 'Views'}</p>
                                                                    </div>

                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    ) : (
                                        <>
                                            {
                                                suggestions.map((s, k) => (
                                                    <div key={k} className={`ai-suggestion-item cursor-pointer ${activeSuggestion === s ? 'active' : ''}`} onClick={() => onClick(s)}>
                                                        <div className='d-flex align-items-center gap-3'>
                                                            {step === 'website_url' ? (
                                                                <img src={`https://www.google.com/s2/favicons?domain=${s}&sz=128`} className='ai-suggestion-icon' alt="" />
                                                            ) : (
                                                                <i className='mdi mdi-instagram d-flex align-items-center justify-content-center border ai-suggestion-icon text-danger'></i>
                                                            )}
                                                            <h6 className='ai-suggestion-item-title m-0'>{step === 'ig' ? '@' : ''}{s}</h6>
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </>
                                    )
                                }
                            </>

                        )
                    }
                </div>
                {/* {suggestions?.length > 0 ? (
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {
                            suggestions.map((suggestion, index) => (
                                <span key={index} className={`onboarding-step-suggestion ${activeSuggestion === suggestion ? 'active' : ''}`} onClick={() => onClick(suggestion)}>
                                    {
                                        step === 'website_url' ? <i className='mdi mdi-web'></i> : (step === 'ig' ? <i className='mdi mdi-instagram'></i> : null)
                                    }
                                    <span>{suggestion}</span>
                                </span>
                            ))
                        }
                    </div>
                ) : (
                    <p className='text-muted'>No suggestions available</p>
                )} */}
            </div>
        </div>
    )
}

export default AISuggestions